import { Message } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { crmOpportunityWorkbenchQueryRootKey } from "../config";
import {
  fetchOpportunitySplitPreview,
  submitOpportunitySplit,
} from "../repo/crmOpportunityWorkbenchRepo";

/** 编排商机拆分预览、分组编辑与提交。 */
export function useCrmOpportunitySplitRuntime(opportunityId: number) {
  const queryClient = useQueryClient();
  const [groupKeys, setGroupKeys] = useState<string[]>([]);
  const [projectGroups, setProjectGroups] = useState<Record<string, string>>({});
  const [originalGroupKey, setOriginalGroupKey] = useState("");
  const previewQuery = useQuery({
    queryKey: [...crmOpportunityWorkbenchQueryRootKey, "split-preview", opportunityId],
    queryFn: () => fetchOpportunitySplitPreview(opportunityId),
  });

  useEffect(() => {
    const preview = previewQuery.data;
    if (!preview) {
      return;
    }
    setGroupKeys(preview.groups.map((group) => group.groupKey));
    setProjectGroups(
      Object.fromEntries(
        preview.groups.flatMap((group) =>
          group.projectIds.map((projectId) => [projectId, group.groupKey]),
        ),
      ),
    );
    setOriginalGroupKey(preview.recommendedOriginalGroupKey);
  }, [previewQuery.data?.previewToken]);

  const groups = useMemo(
    () =>
      groupKeys.map((groupKey) => ({
        groupKey,
        projectIds: (previewQuery.data?.projects ?? [])
          .filter((project) => projectGroups[project.projectId] === groupKey)
          .map((project) => project.projectId),
      })),
    [groupKeys, previewQuery.data?.projects, projectGroups],
  );

  const submitMutation = useMutation({
    mutationFn: (variables: Parameters<typeof submitOpportunitySplit>[0]) =>
      submitOpportunitySplit(variables),
    onSuccess: async (result) => {
      if (result.legacySyncStatus === "success") {
        Message.success("商机拆分已完成，旧系统归组已同步");
      } else if (result.legacySyncStatus === "partial_failed") {
        Message.warning("商机拆分已完成；旧系统归组未完成，需要恢复处理");
      } else {
        Message.warning("商机拆分已完成；旧身份仍需治理，旧系统归组待处理");
      }
      await queryClient.invalidateQueries({ queryKey: crmOpportunityWorkbenchQueryRootKey });
    },
    onError: async (error) => {
      const message = error instanceof Error ? error.message : "商机拆分失败，请稍后重试";
      if (message.includes("变化") || message.includes("409")) {
        Message.warning("数据已变化，正在刷新拆分预览");
        await previewQuery.refetch();
        return;
      }
      Message.error(message);
    },
  });

  return {
    preview: previewQuery.data,
    groups,
    groupKeys,
    projectGroups,
    originalGroupKey,
    loading: previewQuery.isLoading,
    refreshing: previewQuery.isFetching,
    error: previewQuery.error,
    submitting: submitMutation.isPending,
    setOriginalGroupKey,
    moveProject(projectId: string, groupKey: string) {
      const sourceGroupKey = projectGroups[projectId];
      if (
        sourceGroupKey === originalGroupKey &&
        sourceGroupKey !== groupKey &&
        groups.find((group) => group.groupKey === sourceGroupKey)?.projectIds.length === 1
      ) {
        setOriginalGroupKey(groupKey);
      }
      setProjectGroups((current) => ({ ...current, [projectId]: groupKey }));
    },
    addGroup() {
      let index = groupKeys.length + 1;
      let nextKey = `group-${index}`;
      while (groupKeys.includes(nextKey)) {
        index += 1;
        nextKey = `group-${index}`;
      }
      setGroupKeys((current) => [...current, nextKey]);
    },
    removeGroup(groupKey: string) {
      if (groups.find((group) => group.groupKey === groupKey)?.projectIds.length) {
        return;
      }
      setGroupKeys((current) => current.filter((item) => item !== groupKey));
      if (originalGroupKey === groupKey) {
        setOriginalGroupKey(groupKeys.find((item) => item !== groupKey) ?? "");
      }
    },
    refresh() {
      return previewQuery.refetch();
    },
    async submit() {
      const preview = previewQuery.data;
      const nonEmptyGroups = groups.filter((group) => group.projectIds.length > 0);
      if (
        !preview ||
        nonEmptyGroups.length < 2 ||
        !nonEmptyGroups.some((group) => group.groupKey === originalGroupKey)
      ) {
        Message.warning("请保留至少两个非空结果组");
        return;
      }
      await submitMutation.mutateAsync({
        opportunityId,
        payload: {
          previewToken: preview.previewToken,
          originalGroupKey,
          groups: nonEmptyGroups,
        },
      });
    },
  };
}
